import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import MoleculeLoader from '../components/MoleculeLoader';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsentPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [consent, setConsent] = useState(null);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  // Determine if this is a third-party consent form
  const isThirdParty = token?.startsWith('tpconsent_');

  useEffect(() => {
    const endpoint = isThirdParty
      ? `${API_URL}/api/consent-form/${token}`
      : `${API_URL}/api/consent/${token}`;

    fetch(endpoint)
      .then(r => r.json())
      .then(data => {
        if (data.detail) setError(data.detail);
        else {
          setConsent(data);
          setSignerName(isThirdParty ? (data.recipient_name || '') : (data.customer_name || ''));
          if (data.already_signed) setSigned(true);
        }
      })
      .catch(() => setError('Unable to load consent form'))
      .finally(() => setLoading(false));
  }, [token, isThirdParty]);

  const handleSign = async () => {
    if (!agreed || !signerName.trim()) return;
    setSigning(true);
    try {
      const endpoint = isThirdParty
        ? `${API_URL}/api/consent-form/${token}/sign`
        : `${API_URL}/api/consent/${token}/sign`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: signerName.trim() })
      });
      const data = await res.json();
      if (res.ok) setSigned(true);
      else setError(data.detail || 'Failed to sign');
    } catch {
      setError('Failed to submit consent');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <MoleculeLoader size={72} label="Loading consent form…" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Form</h2>
        <p className="text-slate-500">{error}</p>
      </div>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Consent Signed</h2>
        <p className="text-slate-500 mb-2">Thank you for signing the research consent form.</p>
        {isThirdParty && consent?.purchaser_name && (
          <p className="text-sm text-slate-400">Purchase made by: {consent.purchaser_name}</p>
        )}
        <p className="text-sm text-slate-400 mt-2">You may close this page.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-t-2xl p-8 text-center">
          <h1 className="text-2xl font-bold text-white">Research Consent Form</h1>
          <p className="text-teal-200 mt-1">Genora Peptides</p>
        </div>
        
        <div className="bg-white rounded-b-2xl shadow-lg p-8">
          {isThirdParty && consent?.purchaser_name && (
            <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg" data-testid="purchaser-info-banner">
              <p className="text-sm text-teal-800">
                This consent form was sent to you by <strong>{consent.purchaser_name}</strong> who made a purchase on your behalf.
              </p>
            </div>
          )}

          <div className="prose prose-sm max-w-none text-slate-600 space-y-4 mb-8">
            <h3 className="text-lg font-semibold text-slate-900">Research Use Agreement</h3>
            <p>
              By signing this consent form, I acknowledge and agree to the following terms 
              regarding the purchase and use of products from Genora Peptides:
            </p>
            <ol className="list-decimal pl-5 space-y-3">
              <li>
                <strong>Research Purposes Only:</strong> All products purchased from Genora Peptides 
                are intended strictly for <strong>in-vitro research, laboratory testing, and educational purposes only</strong>.
              </li>
              <li>
                <strong>Not for Human Consumption:</strong> I understand that these products are 
                <strong> not intended for human or animal consumption</strong>, therapeutic use, or any form of self-administration.
              </li>
              <li>
                <strong>Qualified Researcher:</strong> I confirm that I am a qualified researcher 
                or am purchasing these products on behalf of a legitimate research institution or laboratory.
              </li>
              <li>
                <strong>Compliance:</strong> I agree to comply with all applicable local, state, 
                and federal laws and regulations regarding the purchase, handling, storage, and use 
                of research chemicals and peptides.
              </li>
              <li>
                <strong>Liability:</strong> I accept full responsibility for the proper handling and 
                use of all products purchased. Genora Peptides shall not be held liable for any misuse 
                of products.
              </li>
            </ol>
          </div>

          <div className="border-t border-slate-200 pt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full legal name"
                data-testid="consent-signer-name"
              />
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer p-4 bg-slate-50 rounded-lg border border-slate-200">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="w-5 h-5 mt-0.5 text-teal-600 rounded"
                data-testid="consent-agree-checkbox"
              />
              <span className="text-sm text-slate-700">
                I have read, understood, and agree to the terms outlined above. I confirm that all products 
                purchased will be used exclusively for research purposes.
              </span>
            </label>

            <Button
              onClick={handleSign}
              disabled={!agreed || !signerName.trim() || signing}
              className="w-full bg-teal-700 hover:bg-teal-800 text-white py-3"
              data-testid="consent-sign-btn"
            >
              {signing ? 'Submitting...' : 'Sign Consent Form'}
            </Button>
            
            <p className="text-xs text-slate-400 text-center">
              Your signature and IP address will be recorded for verification purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentPage;
